import { useQueries } from '@tanstack/react-query';

import { api } from '../../../core/network/api';
import { useAuth } from '../../../global/contexts/auth/auth';
import { auditKeys } from '../queries/keys';
import { auditPaths } from '../queries/paths';
import { TASK_KINDS, TIER_KINDS, readTaskAuthorship, readTierAuthorship } from '../utils/authorship';

// TSS vite/03 — server state for authorship.
//
// One small request per question, each filtered by the entity and ONE kind:
// the route takes `kind` by equality, and a tier's id also tags every event of
// its tasks, so an unfiltered read of a tier would be hundreds of rows to find
// three.
//
// Authorship changes rarely: kept for a few minutes.
const STALE_MS = 5 * 60 * 1000;

function eventQuery(params, enabled) {
    return {
        queryKey: auditKeys.events(params),
        queryFn: ({ signal }) => api
            .get(auditPaths.events(), { params, signal })
            .then(r => r.data?.items ?? []),
        enabled,
        staleTime: STALE_MS,
    };
}

export function useTierAuthorship(tierId) {
    const { can } = useAuth();
    const readable = can('events:read');
    const enabled = readable && !!tierId;

    const [created, edited, agreed] = useQueries({
        queries: [
            eventQuery({ tier_id: tierId, kind: TIER_KINDS.created, limit: 1, offset: 0 }, enabled),
            eventQuery({ tier_id: tierId, kind: TIER_KINDS.edited, limit: 1, offset: 0 }, enabled),
            eventQuery({ tier_id: tierId, kind: TIER_KINDS.agreed, limit: 1, offset: 0 }, enabled),
        ],
    });

    return {
        readable,
        isLoading: [created, edited, agreed].some(q => q.isLoading),
        authorship: readTierAuthorship({
            created: created.data ?? [],
            edited: edited.data ?? [],
            agreed: agreed.data ?? [],
        }),
    };
}

export function useTaskAuthorship(taskId, tierId) {
    const { can } = useAuth();
    const readable = can('events:read');
    const enabled = readable && !!taskId;

    const [created, proposed, proposalUpdated, updated, authorized] = useQueries({
        queries: [
            eventQuery({ task_id: taskId, kind: TASK_KINDS.created, limit: 1, offset: 0 }, enabled),
            // A batch proposal carries no task_id: it is found in its tier, by the
            // ids it names. 100 is the cap, and a tier with more batches than that
            // is one to split. A ONE-OFF task has no tier, and its proposal event
            // does carry the task: it is found by the task.
            eventQuery(
                tierId
                    ? { tier_id: tierId, kind: TASK_KINDS.proposed, limit: 100, offset: 0 }
                    : { task_id: taskId, kind: TASK_KINDS.proposed, limit: 1, offset: 0 },
                enabled,
            ),
            eventQuery({ task_id: taskId, kind: TASK_KINDS.edited[0], limit: 1, offset: 0 }, enabled),
            eventQuery({ task_id: taskId, kind: TASK_KINDS.edited[1], limit: 1, offset: 0 }, enabled),
            eventQuery({ task_id: taskId, kind: TASK_KINDS.authorized, limit: 1, offset: 0 }, enabled),
        ],
    });

    return {
        readable,
        isLoading: [created, proposed, proposalUpdated, updated, authorized].some(q => q.isLoading),
        authorship: readTaskAuthorship({
            created: created.data ?? [],
            proposed: proposed.data ?? [],
            edited: [...(proposalUpdated.data ?? []), ...(updated.data ?? [])],
            authorized: authorized.data ?? [],
        }, taskId),
    };
}
